import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { OutboxEventDto, OutboxEventStatus } from "@/features/outbox/api/dto";

export type OutboxEventRow = OutboxEventDto;

const STATUS_VARIANT: Record<
  OutboxEventStatus,
  "secondary" | "warning" | "success" | "destructive"
> = {
  PENDING: "secondary",
  PROCESSING: "warning",
  DONE: "success",
  DEAD_LETTER: "destructive",
};

/**
 * Colonnes de la boîte d'envoi — contrat uniforme entre les 6 services
 * (guide v2.5 §6.x). Les actions dépendent du service, elles sont donc
 * injectées depuis la page via la cellule `actions`.
 */
export const outboxEventsColumns: ColumnsClarFactory<OutboxEventRow> = () => [
  {
    id: "topic",
    accessorKey: "topic",
    header: t("admin.outbox.columns.topic"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("admin.outbox.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as OutboxEventStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANT[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "attempts",
    accessorKey: "attempts",
    header: t("admin.outbox.columns.attempts"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium tabular-nums">
        {String(getValue() ?? 0)}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "aggregateId",
    accessorKey: "aggregateId",
    header: t("admin.outbox.columns.aggregateId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string | null | undefined) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "nextRetryAt",
    accessorKey: "nextRetryAt",
    header: t("admin.outbox.columns.nextRetryAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string | null | undefined) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "updatedAt",
    accessorKey: "updatedAt",
    header: t("admin.outbox.columns.updatedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string | null | undefined) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "lastErrorMessage",
    accessorKey: "lastErrorMessage",
    header: t("admin.outbox.columns.lastError"),
    cell: ({ getValue }) => (
      <span className="text-xs text-destructive line-clamp-2 max-w-xs">
        {(getValue() as string | null | undefined) || "—"}
      </span>
    ),
    meta: { type: "denied" },
  },
];
